import type { ProtectMerchantsPortalRouter } from "@src/backend/src/protect/functions/merchantsPortal";
import type { inferRouterInputs, inferRouterOutputs } from "@trpc/server";
import type { Dayjs } from "dayjs";
import { Contract } from "./types";

type ProtectRouterInputs = inferRouterInputs<ProtectMerchantsPortalRouter>;
type ProtectRouterOutputs = inferRouterOutputs<ProtectMerchantsPortalRouter>;

export type ChangeResult =
  ProtectRouterOutputs["contracts"]["renew"]["results"][number];
export type ChangeRejection = NonNullable<ChangeResult["error"]>;
export type ChangeRules = ProtectRouterOutputs["contracts"]["changeRules"];
export type RetirementReason =
  ProtectRouterInputs["contracts"]["retire"]["items"][number]["reason"];

export const retirementReasonLabels: Record<RetirementReason, string> = {
  contractEnded: "Terminación por vencimiento",
  mutualAgreement: "Mutuo acuerdo",
  tenantBreach: "Incumplimiento del arrendatario",
  propertySold: "Venta del inmueble",
  insurerTransfer: "Traslado de afianzadora",
  other: "Otro",
};

export const changeRejectionMessages: Record<ChangeRejection, string> = {
  notFound: "No encontramos este contrato.",
  notEnrolled: "El contrato no está afianzado.",
  rentDecrease: "El nuevo canon no puede ser menor al actual.",
  scheduledRenewal: "Ya tiene una renovación programada.",
  scheduledRetirement: "Tiene un retiro programado.",
  endDateDecrease: "La nueva fecha fin no puede ser anterior a la actual.",
  noChange: "No hay cambios: sube el canon o extiende la fecha fin.",
  ipcUnavailable:
    "El IPC del año aún no está cargado. Escríbenos para aplicar este aumento.",
  increaseCapExceeded: "Supera el tope de aumento.",
  increaseTooSoon: "Ya tuvo un aumento en los últimos 12 meses.",
  hasOpenClaim:
    "Tiene una reclamación abierta. Para retirarlo, contacta a tu asesor.",
  noteRequired: "Escribe el motivo del retiro.",
  conflict: "El contrato cambió mientras lo procesábamos. Intenta de nuevo.",
  failed: "No pudimos procesar este contrato. Intenta de nuevo.",
};

export const canChange = (contract: Contract) => contract.status === "enrolled";

export const canDownloadCertificate = (contract: Contract) =>
  contract.status !== "retired";

export const increaseCap = (contract: Contract, rules: ChangeRules) =>
  contract.propertyUse === "commercial"
    ? rules.commercialMaxIncrease
    : rules.ipc;

export const maxRent = (monthlyRent: number, cap: number) =>
  Math.floor((monthlyRent * Math.round((1 + cap) * 1_000_000)) / 1_000_000);

export const isExpired = (contract: Contract, today: Dayjs) =>
  contract.rentalsContractEndDate < today.format("YYYY-MM-DD");

export const formatPercent = (fraction: number) =>
  `${(fraction * 100).toLocaleString("es-CO", {
    maximumFractionDigits: 2,
  })}%`;
